import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Linkedin, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT_HEADING = "'Cormorant Garamond', Cambria, Georgia, serif";
const FONT_BODY = "'Jost', sans-serif";

export default function LeadershipPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${API}/leadership`);
        if (res.ok) setMembers(await res.json());
      } catch {}
      setLoading(false);
    };
    fetch_();
  }, []);

  return (
    <div className="min-h-screen bg-[#1A1A1A]" data-testid="leadership-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 md:px-12 py-16 pt-28">
        <div className="text-center mb-20">
          <p className="text-[#C9A96E] text-sm uppercase tracking-[0.2em] font-semibold mb-4" style={{ fontFamily: FONT_BODY }}>Our People</p>
          <h1 className="text-4xl md:text-6xl text-[#FAF8F5] mb-6 tracking-tight" style={{ fontFamily: FONT_HEADING }}>Leadership Team</h1>
          <div className="w-16 h-[1px] bg-[#C9A96E] mx-auto mb-6" />
          <p className="text-[#999] text-lg max-w-2xl mx-auto" style={{ fontFamily: FONT_BODY }}>
            The people behind Sàwai Capital — experienced deal originators, investors, and operators building India's private deal network.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#A0743A] border-t-transparent rounded-full animate-spin" /></div>
        ) : members.length === 0 ? (
          <div className="text-center py-20 border border-[#A0743A]/10">
            <p className="text-[#999] text-lg" style={{ fontFamily: FONT_BODY }}>Leadership profiles coming soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10" data-testid="leadership-grid">
            {members.map(member => (
              <div key={member.member_id} className="group text-center" data-testid={`leader-${member.member_id}`}>
                {/* Photo */}
                <div className="relative w-56 h-56 mx-auto mb-8 overflow-hidden">
                  <div className="absolute inset-0 border border-[#A0743A]/30 group-hover:border-[#A0743A] transition-colors" />
                  {member.photo ? (
                    <img
                      src={member.photo}
                      alt={member.name}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                    />
                  ) : (
                    <div className="w-full h-full bg-[#A0743A]/10 flex items-center justify-center">
                      <span className="text-5xl text-[#A0743A]/40" style={{ fontFamily: FONT_HEADING }}>{member.name?.charAt(0)}</span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <h3 className="text-2xl text-[#FAF8F5] mb-1" style={{ fontFamily: FONT_HEADING }}>{member.name}</h3>
                <p className="text-[#A0743A] text-sm uppercase tracking-[0.15em] font-semibold mb-4" style={{ fontFamily: FONT_BODY }}>{member.title}</p>

                {member.bio && (
                  <p className="text-[#999] text-sm leading-relaxed mb-6 max-w-xs mx-auto" style={{ fontFamily: FONT_BODY }}>{member.bio}</p>
                )}

                {/* Social Links */}
                <div className="flex items-center justify-center gap-4">
                  {member.linkedin && (
                    <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="text-[#A0743A] hover:text-[#C9A96E] transition-colors" data-testid={`linkedin-${member.member_id}`}>
                      <Linkedin size={18} strokeWidth={1.25} />
                    </a>
                  )}
                  {member.email && (
                    <a href={`mailto:${member.email}`} className="text-[#A0743A] hover:text-[#C9A96E] transition-colors" data-testid={`email-${member.member_id}`}>
                      <Mail size={18} strokeWidth={1.25} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
